import { supabase } from '../lib/supabase'
import { geminiService } from './geminiService'
import { QuizQuestion, LearningVideo, GenerateQuizRequest } from '../types/learning'

class QuizGeneratorService {
  // Generate quiz questions using Gemini AI
  async generateQuizFromVideo(videoInfo: {
    title: string
    description?: string
    transcript?: string
    learningObjectives: string[]
    category: string
    difficulty: string
  }): Promise<QuizQuestion[]> {
    try {
      console.log('Generating quiz questions for video:', videoInfo.title)
      
      const questions = await geminiService.generateQuizQuestions(videoInfo)
      
      console.log(`Generated ${questions.length} quiz questions`)
      return questions
    } catch (error) {
      console.error('Error generating quiz from video:', error)
      throw new Error('Failed to generate quiz questions')
    }
  }

  // Generate a single question for a specific topic
  async generateQuestion(topic: string, difficulty: string): Promise<QuizQuestion> {
    try {
      const videoInfo = {
        title: `Educational content about ${topic}`,
        description: `Learn about ${topic}`,
        learningObjectives: [`Understand ${topic}`],
        category: 'educational',
        difficulty: difficulty
      }

      const questions = await this.generateQuizFromVideo(videoInfo)
      return questions[0] // Return the first question
    } catch (error) {
      console.error('Error generating single question:', error)
      throw new Error('Failed to generate question')
    }
  }

  // Validate quiz quality
  async validateQuizQuality(questions: QuizQuestion[]): Promise<boolean> {
    try {
      // Basic validation checks
      if (!questions || questions.length === 0) {
        console.log('Validation failed: No questions provided')
        return false
      }

      if (questions.length !== 5) {
        console.log(`Validation failed: Expected 5 questions, got ${questions.length}`)
        return false
      }

      // Check question types distribution
      const multipleChoice = questions.filter(q => q.question_type === 'multiple-choice').length
      const trueFalse = questions.filter(q => q.question_type === 'true-false').length

      if (multipleChoice < 2 || multipleChoice > 4) {
        console.log(`Validation failed: Expected 2-4 multiple choice questions, got ${multipleChoice}`)
        return false
      }

      if (trueFalse < 1 || trueFalse > 3) {
        console.log(`Validation failed: Expected 1-3 true/false questions, got ${trueFalse}`)
        return false
      }

      // Check difficulty distribution
      const easy = questions.filter(q => q.difficulty === 'easy').length
      const medium = questions.filter(q => q.difficulty === 'medium').length
      const hard = questions.filter(q => q.difficulty === 'hard').length

      if (easy < 1 || medium < 1) {
        console.log(`Validation failed: Need at least 1 easy and 1 medium question`)
        return false
      }

      // Validate individual questions
      for (let i = 0; i < questions.length; i++) {
        const question = questions[i]
        
        if (!question.question_text || question.question_text.trim().length < 10) {
          console.log(`Validation failed: Question ${i + 1} has invalid text`)
          return false
        }

        if (!question.correct_answer || question.correct_answer.trim().length === 0) {
          console.log(`Validation failed: Question ${i + 1} has no correct answer`)
          return false
        }

        if (question.question_type === 'multiple-choice') {
          if (!question.options || question.options.length !== 4) {
            console.log(`Validation failed: Question ${i + 1} doesn't have 4 options`)
            return false
          }
          
          // Check if correct answer is in options
          if (!question.options.includes(question.correct_answer)) {
            console.log(`Validation failed: Question ${i + 1} correct answer not in options`)
            return false
          }
        }

        if (!question.explanation || question.explanation.trim().length < 5) {
          console.log(`Validation failed: Question ${i + 1} has no explanation`)
          return false
        }
      }

      console.log('Quiz validation passed')
      return true
    } catch (error) {
      console.error('Error validating quiz quality:', error)
      return false
    }
  }

  // Save quiz questions to database
  async saveQuizQuestions(videoId: string, questions: QuizQuestion[]): Promise<QuizQuestion[]> {
    try {
      console.log(`Saving ${questions.length} quiz questions for video ${videoId}`)

      // Prepare questions for database insertion
      const questionsToInsert = questions.map(q => ({
        video_id: videoId,
        question_text: q.question_text,
        question_type: q.question_type,
        options: q.options,
        correct_answer: q.correct_answer,
        explanation: q.explanation,
        difficulty: q.difficulty,
        order_index: q.order_index
      }))

      // Insert questions into database
      const { data, error } = await supabase
        .from('quiz_questions')
        .insert(questionsToInsert)
        .select()

      if (error) {
        console.error('Error saving quiz questions:', error)
        throw error
      }

      console.log(`Successfully saved ${data.length} quiz questions`)
      return data
    } catch (error) {
      console.error('Error saving quiz questions to database:', error)
      throw new Error('Failed to save quiz questions')
    }
  }

  // Generate and save quiz for a video
  async generateAndSaveQuiz(videoId: string, videoInfo: {
    title: string
    description?: string
    transcript?: string
    learningObjectives: string[]
    category: string
    difficulty: string
  }): Promise<QuizQuestion[]> {
    try {
      // Check if quiz already exists
      const existingQuestions = await this.getExistingQuizQuestions(videoId)
      if (existingQuestions.length > 0) {
        console.log(`Quiz already exists for video ${videoId} with ${existingQuestions.length} questions`)
        return existingQuestions
      }

      // Generate new quiz questions
      const questions = await this.generateQuizFromVideo(videoInfo)

      // Validate quality
      const isValid = await this.validateQuizQuality(questions)
      if (!isValid) {
        console.warn('Generated quiz failed quality validation')
        // Still save the questions, but log the warning
      }

      // Save to database
      const savedQuestions = await this.saveQuizQuestions(videoId, questions)
      
      return savedQuestions
    } catch (error) {
      console.error('Error generating and saving quiz:', error)
      throw new Error('Failed to generate and save quiz')
    }
  }

  // Get existing quiz questions for a video
  async getExistingQuizQuestions(videoId: string): Promise<QuizQuestion[]> {
    try {
      const { data, error } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('video_id', videoId)
        .order('order_index')

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching existing quiz questions:', error)
      return []
    }
  }

  // Update existing quiz question
  async updateQuizQuestion(questionId: string, updates: Partial<QuizQuestion>): Promise<QuizQuestion> {
    try {
      const { data, error } = await supabase
        .from('quiz_questions')
        .update(updates)
        .eq('id', questionId)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating quiz question:', error)
      throw new Error('Failed to update quiz question')
    }
  }

  // Delete quiz question
  async deleteQuizQuestion(questionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('quiz_questions')
        .delete()
        .eq('id', questionId)

      if (error) throw error
      console.log(`Deleted quiz question ${questionId}`)
    } catch (error) {
      console.error('Error deleting quiz question:', error)
      throw new Error('Failed to delete quiz question')
    }
  }

  // Delete all quiz questions for a video
  async deleteQuizForVideo(videoId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('quiz_questions')
        .delete()
        .eq('video_id', videoId)

      if (error) throw error
      console.log(`Deleted all quiz questions for video ${videoId}`)
    } catch (error) {
      console.error('Error deleting quiz for video:', error)
      throw new Error('Failed to delete quiz for video')
    }
  }

  // Regenerate quiz for a video (delete old and create new)
  async regenerateQuiz(videoId: string, videoInfo: {
    title: string
    description?: string
    transcript?: string
    learningObjectives: string[]
    category: string
    difficulty: string
  }): Promise<QuizQuestion[]> {
    try {
      console.log(`Regenerating quiz for video ${videoId}`)

      // Delete existing quiz
      await this.deleteQuizForVideo(videoId)

      // Generate and save new quiz
      const newQuestions = await this.generateAndSaveQuiz(videoId, videoInfo)
      
      console.log(`Successfully regenerated quiz with ${newQuestions.length} questions`)
      return newQuestions
    } catch (error) {
      console.error('Error regenerating quiz:', error)
      throw new Error('Failed to regenerate quiz')
    }
  }

  // Get quiz statistics
  async getQuizStats(videoId: string): Promise<{
    totalQuestions: number
    questionTypes: Record<string, number>
    difficultyDistribution: Record<string, number>
    averageAttempts?: number
    averageScore?: number
  }> {
    try {
      // Get quiz questions
      const questions = await this.getExistingQuizQuestions(videoId)
      
      if (questions.length === 0) {
        return {
          totalQuestions: 0,
          questionTypes: {},
          difficultyDistribution: {}
        }
      }

      // Calculate statistics
      const questionTypes = questions.reduce((acc, q) => {
        acc[q.question_type] = (acc[q.question_type] || 0) + 1
        return acc
      }, {} as Record<string, number>)

      const difficultyDistribution = questions.reduce((acc, q) => {
        acc[q.difficulty] = (acc[q.difficulty] || 0) + 1
        return acc
      }, {} as Record<string, number>)

      // Get attempt statistics (if any)
      const { data: attempts } = await supabase
        .from('user_quiz_attempts')
        .select('score, total_questions')
        .eq('video_id', videoId)

      let averageAttempts = 0
      let averageScore = 0

      if (attempts && attempts.length > 0) {
        averageAttempts = attempts.length
        averageScore = attempts.reduce((sum, attempt) => {
          return sum + (attempt.score / attempt.total_questions) * 100
        }, 0) / attempts.length
      }

      return {
        totalQuestions: questions.length,
        questionTypes,
        difficultyDistribution,
        averageAttempts,
        averageScore: Math.round(averageScore)
      }
    } catch (error) {
      console.error('Error getting quiz stats:', error)
      return {
        totalQuestions: 0,
        questionTypes: {},
        difficultyDistribution: {}
      }
    }
  }

  // Bulk generate quizzes for multiple videos
  async bulkGenerateQuizzes(videos: Array<{
    id: string
    title: string
    description?: string
    transcript?: string
    learningObjectives: string[]
    category: string
    difficulty: string
  }>): Promise<Record<string, QuizQuestion[]>> {
    try {
      console.log(`Bulk generating quizzes for ${videos.length} videos`)

      const results: Record<string, QuizQuestion[]> = {}

      for (const video of videos) {
        try {
          console.log(`Generating quiz for: ${video.title}`)
          const questions = await this.generateAndSaveQuiz(video.id, video)
          results[video.id] = questions
          
          // Add small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 1000))
        } catch (error) {
          console.error(`Failed to generate quiz for video ${video.id}:`, error)
          results[video.id] = []
        }
      }

      console.log(`Bulk generation complete. Generated quizzes for ${Object.keys(results).length} videos`)
      return results
    } catch (error) {
      console.error('Error in bulk quiz generation:', error)
      throw new Error('Failed to bulk generate quizzes')
    }
  }
}

// Create and export singleton instance
export const quizGeneratorService = new QuizGeneratorService()
export default QuizGeneratorService
